/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-nested-ternary */
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
  ForwardRefExoticComponent,
  RefAttributes,
} from 'react';
import {
  Button,
  Flex,
  Spinner,
  Text,
  SimpleGrid,
  Box,
  Card,
  CardBody,
  HStack,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { createSearchParams } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  CheckCircle,
  WarningCircle,
  Package,
  FunnelSimple,
  Buildings,
  CurrencyDollar,
  XCircle,
  type IconProps,
} from 'phosphor-react';
import { FaFileInvoiceDollar } from 'react-icons/fa6';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../components/SearchModeBar';
import SelectInputForm from '../../../../components/inputs/forms/SelectInputForm';
import { IData, ISelect } from '../../../../types/shared';
import { ICompany, useCompanies } from '../../../../hooks/useCompanies';
import { IClient } from '../../../../hooks/useClients';
import { formatCNPJ, getFormattedLabelString, toCurrency } from '../../../../utils/FormatFunctions';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { PageBox } from '../../../../components/pageBox';
import { api } from '../../../../services/api';
import { FilterDrawer } from '../../../../components/FilterDrawer';
import ViewNotaModal from '../../../../components/viewNota';

moment.locale('pt-br');

export function ModulesHomeIntegrationBillingAnalysisPage() {
  const { getCompaniesFilters, getSituationsTotals } = useCompanies();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [searchMode, setSearchMode] = useState<'ID' | 'Nome' | 'CNPJ'>('ID');
  const searchModeRef = useRef(searchMode);

  // Ref para o observer do scroll infinito
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  const {
    currentDate: currentFilterDate,
    dateStart,
    dateEnd,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    filters,
    setFilters,
  } = useMonthFilter({
    additionalParams: { search: '', companyId: '', screen_data: 'faturamento' },
  });

  const filtersFormMethods = useForm<{
    search: string;
    cnpj: string;
    companyId: any;
    companies: ICompany;
    order_by: ISelect<string>;
    order: string;
    cliente: IClient;
    cidade: ISelect<string>;
    uf: ISelect<string>;
    grupo_de_empresas: ISelect<string>;
    tags: ISelect<string>;
    comercial: ISelect<string>;
    contabil: ISelect<string>;
    financeiro: ISelect<string>;
    contas_a_pagar: ISelect<string>;
    encerramento_anual: ISelect<string>;
    fiscal: ISelect<string>;
    imposto_de_renda: ISelect<string>;
    integracao_contabil: ISelect<string>;
    integracao_fiscal: ISelect<string>;
    pessoal: ISelect<string>;
    relacionamento_com_cliente: ISelect<string>;
    societario: ISelect<string>;
    situacao: 'ok' | 'aviso' | 'erro' | 'outro' | null;
  }>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? filters.get('external_id') ?? '',
      cnpj: filters.get('cnpj') ?? '',
    },
  });

  // Verificar se há filtros ativos (excluindo campos básicos)
  const hasActiveFilters = Array.from(filters.keys()).some(
    key =>
      key !== 'page' &&
      key !== 'size' &&
      key !== 'date_start' &&
      key !== 'date_end' &&
      key !== 'situacao' &&
      key !== 'has_billing' &&
      key !== 'order' &&
      key !== 'order_by' &&
      key !== 'search' &&
      key !== 'cnpj' &&
      key !== 'external_id' &&
      key !== 'screen_data' &&
      filters.get(key),
  );

  // Debounce para o campo de busca
  const debouncedSearch = useDebounceCallback((searchValue: string) => {
    const newFilters = new URLSearchParams(filters);
    const currentMode = searchModeRef.current;

    // Remove todos os parâmetros de busca anteriores
    newFilters.delete('search');
    newFilters.delete('cnpj');
    newFilters.delete('external_id');

    if (searchValue) {
      switch (currentMode) {
        case 'ID':
          newFilters.set('external_id', searchValue);
          break;
        case 'Nome':
          newFilters.set('search', searchValue);
          break;
        case 'CNPJ':
          newFilters.set('cnpj', searchValue.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
          break;
        default:
          break;
      }
    }

    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  // Re-executa busca quando muda o modo
  useEffect(() => {
    debouncedSearch(filtersFormMethods.getValues().search || '');
  }, [searchMode]);

  // Função para aplicar ordenação imediatamente
  const handleOrderChange = (ordem: string | undefined) => {
    const newFilters = new URLSearchParams(filters);
    newFilters.set('page', '1');

    if (ordem === 'Maior variação de faturamento') {
      newFilters.set('order', 'desc');
      newFilters.set('order_by', 'variacao');
    } else if (ordem === 'Menor variação de faturamento') {
      newFilters.set('order', 'asc');
      newFilters.set('order_by', 'variacao');
    } else if (ordem === 'Maior faturamento atual') {
      newFilters.set('order', 'desc');
      newFilters.set('order_by', 'atual');
    } else if (ordem === 'Menor faturamento atual') {
      newFilters.set('order', 'asc');
      newFilters.set('order_by', 'atual');
    } else if (ordem === 'Maior faturamento anterior') {
      newFilters.set('order', 'desc');
      newFilters.set('order_by', 'anterior');
    } else if (ordem === 'Menor faturamento anterior') {
      newFilters.set('order', 'asc');
      newFilters.set('order_by', 'anterior');
    } else {
      newFilters.delete('order');
      newFilters.delete('order_by');
    }

    setFilters(newFilters);
  };

  // Watch nos campos search e order_by
  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      if (name === 'search') {
        debouncedSearch(values.search || '');
      }
      if (name === 'order_by') {
        handleOrderChange(values.order_by?.value);
      }
    });

    return () => subscription.unsubscribe();
  }, [filtersFormMethods.watch, debouncedSearch]);

  // Função para aplicar os filtros quando clica no botão "Filtrar"
  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    const newFilters = new URLSearchParams();

    // Mantém filtros básicos
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    newFilters.set('search', filters.get('search') || '');
    if (filters.get('cnpj')) newFilters.set('cnpj', filters.get('cnpj') || '');
    if (filters.get('external_id')) newFilters.set('external_id', filters.get('external_id') || '');
    newFilters.set('screen_data', 'faturamento');
    newFilters.set(
      'date_start',
      filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
    );
    newFilters.set(
      'date_end',
      filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
    );

    // Mantém situacao e has_billing se existirem
    const situacao = filters.get('situacao');
    const hasBilling = filters.get('has_billing');
    if (situacao) newFilters.set('situacao', situacao);
    if (hasBilling) newFilters.set('has_billing', hasBilling);

    // Mantém ordenação se existir
    const orderBy = filters.get('order_by');
    const order = filters.get('order');
    if (orderBy) newFilters.set('order_by', orderBy);
    if (order) newFilters.set('order', order);

    // Aplica filtros avançados
    if (values.companies?.external_id) newFilters.set('id', values.companies.external_id);
    if (values.cidade?.value) newFilters.set('cidade', values.cidade.value);
    if (values.uf?.value) newFilters.set('uf', values.uf.value);
    if (values.grupo_de_empresas?.value)
      newFilters.set('grupo_de_empresas', values.grupo_de_empresas.value);
    if (values.tags?.value) newFilters.set('tags', values.tags.value);
    if (values.comercial?.value) newFilters.set('comercial', values.comercial.value);
    if (values.contabil?.value) newFilters.set('contabil', values.contabil.value);
    if (values.financeiro?.value) newFilters.set('financeiro', values.financeiro.value);
    if (values.contas_a_pagar?.value) newFilters.set('contas_a_pagar', values.contas_a_pagar.value);
    if (values.encerramento_anual?.value)
      newFilters.set('encerramento_anual', values.encerramento_anual.value);
    if (values.fiscal?.value) newFilters.set('fiscal', values.fiscal.value);
    if (values.imposto_de_renda?.value)
      newFilters.set('imposto_de_renda', values.imposto_de_renda.value);
    if (values.integracao_contabil?.value)
      newFilters.set('integracao_contabil', values.integracao_contabil.value);
    if (values.integracao_fiscal?.value)
      newFilters.set('integracao_fiscal', values.integracao_fiscal.value);
    if (values.pessoal?.value) newFilters.set('pessoal', values.pessoal.value);
    if (values.relacionamento_com_cliente?.value)
      newFilters.set('relacionamento_com_cliente', values.relacionamento_com_cliente.value);
    if (values.societario?.value) newFilters.set('societario', values.societario.value);

    setFilters(newFilters);
  };

  // Criar filtros para totais sem has_billing e situacao (para sempre mostrar valores globais nos cards)
  const filtersForTotals = useMemo(() => {
    const totalsFilters = new URLSearchParams(Object.fromEntries(filters));
    totalsFilters.delete('has_billing'); // Remove filtro de faturamento dos totais
    totalsFilters.delete('situacao'); // Remove filtro de situação dos totais
    return cleanFilters(Object.fromEntries([...Array.from(totalsFilters)]));
  }, [filters]);

  const queryKeySituacoesTotais = ['getSituationsTotals-billingAnalysis', filtersForTotals];
  const { data: dataCompaniesSituacoes, isFetching: isFetchingSituacoesTotais } = useQuery<any>({
    queryKey: queryKeySituacoesTotais,
    queryFn: getSituationsTotals,
  });
  const situacoesTotais = dataCompaniesSituacoes;

  const queryKeyCompaniesFilters = ['CompaniesFilters'];
  const { data: CompaniesFiltersData } = useQuery({
    queryKey: queryKeyCompaniesFilters,
    queryFn: getCompaniesFilters,
  });

  const CompaniesFilters = CompaniesFiltersData;

  const queryKey = [
    'CompaniesList-billingAnalysis',
    cleanFilters(Object.fromEntries([...Array.from(filters)])),
  ];

  const fetchCompanies = async ({ pageParam = 1 }) => {
    const filterParams = cleanFilters(Object.fromEntries([...Array.from(filters)]));
    const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', {
      params: { ...filterParams, page: pageParam, size: 50 },
    });
    return res.data;
  };

  const {
    data,
    isFetching: isFetchingCompanyList,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: fetchCompanies,
    initialPageParam: 1,
    getNextPageParam: lastPage => lastPage.data.next_page,
  });

  // Combina todas as páginas em uma única lista, removendo duplicatas pelo ID
  const allCompanies = useMemo(() => {
    if (!data?.pages) return [];
    const seenIds = new Set<number>();
    const companiesData: ICompany[] = [];
    data.pages.forEach(page => {
      page.data.items.forEach(company => {
        if (!seenIds.has(company.id)) {
          seenIds.add(company.id);
          companiesData.push(company);
        }
      });
    });
    return companiesData;
  }, [data?.pages]);

  const totalCompanies = data?.pages?.[0]?.data?.total ?? 0;

  // Configura o IntersectionObserver para scroll infinito
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage],
  );

  const situacaoFilter = filters.get('situacao');

  // Calcular porcentagens para os cards
  const totalEmpresasInScope = situacoesTotais?.total_empresas ?? 0;
  const percentOk =
    totalEmpresasInScope > 0 ? ((situacoesTotais?.ok ?? 0) / totalEmpresasInScope) * 100 : 0;
  const percentAviso =
    totalEmpresasInScope > 0 ? ((situacoesTotais?.aviso ?? 0) / totalEmpresasInScope) * 100 : 0;
  const percentErro =
    totalEmpresasInScope > 0 ? ((situacoesTotais?.erro ?? 0) / totalEmpresasInScope) * 100 : 0;
  const percentOutro =
    totalEmpresasInScope > 0 ? ((situacoesTotais?.outros ?? 0) / totalEmpresasInScope) * 100 : 0;
  const percentComFaturamento =
    totalEmpresasInScope > 0
      ? ((situacoesTotais?.total_com_faturamento ?? 0) / totalEmpresasInScope) * 100
      : 0;
  const percentSemFaturamento =
    totalEmpresasInScope > 0
      ? ((situacoesTotais?.total_sem_faturamento ?? 0) / totalEmpresasInScope) * 100
      : 0;

  // Configuração de cores e ícones para cada tipo de card
  const cardConfig = {
    ok: {
      icon: CheckCircle,
      iconColor: '#16DBCC',
      borderColor: '#10B981',
    },
    aviso: {
      icon: WarningCircle,
      iconColor: '#F59E0B',
      borderColor: '#FBBF24',
    },
    erro: {
      icon: WarningCircle,
      iconColor: '#EF4444',
      borderColor: '#F87171',
    },
    outro: {
      icon: Package,
      iconColor: '#6B7280',
      borderColor: '#9CA3AF',
    },
    comFaturamento: {
      icon: CurrencyDollar,
      iconColor: '#10B981',
      borderColor: '#22C55E',
    },
    semFaturamento: {
      icon: XCircle,
      iconColor: '#EF4444',
      borderColor: '#F87171',
    },
    totalEmpresas: {
      icon: Buildings,
      iconColor: '#6366F1',
      borderColor: '#818CF8',
    },
  };

  const handleClearFilters = () => {
    const clearedFilters = createSearchParams({
      page: '1',
      size: '50',
      search: filters.get('search') || '',
      date_start: filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
      date_end: filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
      screen_data: 'faturamento',
    });
    // Mantém situacao, has_billing, cnpj e external_id se existirem
    const situacao = filters.get('situacao');
    const hasBilling = filters.get('has_billing');
    const cnpj = filters.get('cnpj');
    const externalId = filters.get('external_id');
    if (situacao) clearedFilters.set('situacao', situacao);
    if (hasBilling) clearedFilters.set('has_billing', hasBilling);
    if (cnpj) clearedFilters.set('cnpj', cnpj);
    if (externalId) clearedFilters.set('external_id', externalId);

    setFilters(clearedFilters);
    // Limpar cada campo individualmente para forçar atualização visual
    const { setValue } = filtersFormMethods;
    setValue('companies', null as any);
    setValue('cidade', null as any);
    setValue('uf', null as any);
    setValue('grupo_de_empresas', null as any);
    setValue('tags', null as any);
    setValue('comercial', null as any);
    setValue('contabil', null as any);
    setValue('financeiro', null as any);
    setValue('contas_a_pagar', null as any);
    setValue('encerramento_anual', null as any);
    setValue('fiscal', null as any);
    setValue('imposto_de_renda', null as any);
    setValue('integracao_contabil', null as any);
    setValue('integracao_fiscal', null as any);
    setValue('pessoal', null as any);
    setValue('relacionamento_com_cliente', null as any);
    setValue('societario', null as any);
  };

  // Função auxiliar para adicionar transparência à cor
  const addAlphaToColor = (color: string, alpha: number): string => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Componente para card de total
  const TotalCard = ({
    label,
    value,
    percent,
    isActive,
    onClick,
    icon: IconComponent,
    iconColor,
    borderColor,
  }: {
    label: string;
    value: number;
    percent?: string;
    isActive?: boolean;
    onClick?: () => void;
    icon?: ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;
    iconColor?: string;
    borderColor?: string;
  }) => {
    const finalBorderColor = isActive ? borderColor || iconColor || '#0077B6' : 'border';
    const finalBorderWidth = isActive ? '2px solid' : '1px solid';
    const iconBgColor = iconColor ? addAlphaToColor(iconColor, 0.15) : 'transparent';

    return (
      <Card
        as={onClick ? 'button' : 'div'}
        onClick={onClick}
        cursor={onClick ? 'pointer' : 'default'}
        bg="body"
        border={finalBorderWidth}
        borderColor={finalBorderColor}
        borderRadius="16px"
        transition="all 0.2s"
        _hover={onClick ? { transform: 'translateY(-2px)', shadow: 'md' } : {}}
        h="full"
        position="relative"
        overflow="hidden"
      >
        <CardBody p={{ base: 3, md: 4 }}>
          <Flex flexDirection="column" gap={1.5}>
            {IconComponent && iconColor && (
              <Flex alignItems="center" justifyContent="flex-start" mb={0.5} gap={2}>
                <Box
                  p={1.5}
                  borderRadius="md"
                  bg={iconBgColor}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <IconComponent size={18} color={iconColor} weight="duotone" />
                </Box>
                <Text fontSize={{ base: '11px', md: '12px' }} color="#718EBF" fontWeight="medium">
                  {label}
                </Text>
              </Flex>
            )}
            {!IconComponent && (
              <Text fontSize={{ base: '11px', md: '12px' }} color="#718EBF">
                {label}
              </Text>
            )}
            <Text fontSize={{ base: '14px', md: '16px' }} fontWeight="bold" color="#1A202C">
              {value}
            </Text>
            {percent && (
              <Text fontSize={{ base: '9px', md: '11px' }} color="#94A3B8">
                {percent}
              </Text>
            )}
          </Flex>
        </CardBody>
      </Card>
    );
  };

  return (
    <PageBox>
      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        {/* Header */}
        <Flex
          flexDirection={{ base: 'column', lg: 'row' }}
          alignItems={{ base: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
          gap={4}
          mb={6}
        >
          <Flex alignItems="center" gap={3}>
            <ReturnButton previousPage="modulesHome/integration" />
            <Flex
              alignItems="center"
              justifyContent="center"
              borderRadius="full"
              bg="#ABEDC6"
              p={3}
            >
              <FaFileInvoiceDollar color="#219653" size={32} />
            </Flex>
            <Text fontWeight="bold" fontSize={{ base: '18px', md: '20px' }}>
              Análise de Faturamento
            </Text>
          </Flex>
        </Flex>

        {/* Cards de Situação */}
        {isFetchingSituacoesTotais ? (
          <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={4} py={8}>
            <Spinner size="lg" color="primary" />
            <Text fontSize="xl" fontWeight="semibold">
              Carregando...
            </Text>
          </Flex>
        ) : (
          <SimpleGrid
            columns={{ base: 2, md: 3, lg: 4, xl: 7 }}
            spacing={{ base: 3, md: 4 }}
            mb={6}
          >
            <TotalCard
              label="Ok"
              value={situacoesTotais?.ok ?? 0}
              percent={`${percentOk.toFixed(1)}%`}
              isActive={situacaoFilter === 'ok'}
              icon={cardConfig.ok.icon}
              iconColor={cardConfig.ok.iconColor}
              borderColor={cardConfig.ok.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (situacaoFilter === 'ok') {
                  newFilters.delete('situacao');
                } else {
                  newFilters.set('page', '1');
                  newFilters.set('situacao', 'ok');
                  newFilters.delete('has_billing'); // Remove filtro de faturamento
                }
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Aviso"
              value={situacoesTotais?.aviso ?? 0}
              percent={`${percentAviso.toFixed(1)}%`}
              isActive={situacaoFilter === 'aviso'}
              icon={cardConfig.aviso.icon}
              iconColor={cardConfig.aviso.iconColor}
              borderColor={cardConfig.aviso.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (situacaoFilter === 'aviso') {
                  newFilters.delete('situacao');
                } else {
                  newFilters.set('page', '1');
                  newFilters.set('situacao', 'aviso');
                  newFilters.delete('has_billing'); // Remove filtro de faturamento
                }
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Erro"
              value={situacoesTotais?.erro ?? 0}
              percent={`${percentErro.toFixed(1)}%`}
              isActive={situacaoFilter === 'erro'}
              icon={cardConfig.erro.icon}
              iconColor={cardConfig.erro.iconColor}
              borderColor={cardConfig.erro.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (situacaoFilter === 'erro') {
                  newFilters.delete('situacao');
                } else {
                  newFilters.set('page', '1');
                  newFilters.set('situacao', 'erro');
                  newFilters.delete('has_billing'); // Remove filtro de faturamento
                }
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Auditadas"
              value={situacoesTotais?.outros ?? 0}
              percent={`${percentOutro.toFixed(1)}%`}
              isActive={situacaoFilter === 'outro'}
              icon={cardConfig.outro.icon}
              iconColor={cardConfig.outro.iconColor}
              borderColor={cardConfig.outro.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (situacaoFilter === 'outro') {
                  newFilters.delete('situacao');
                } else {
                  newFilters.set('page', '1');
                  newFilters.set('situacao', 'outro');
                  newFilters.delete('has_billing'); // Remove filtro de faturamento
                }
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Com Faturamento"
              value={situacoesTotais?.total_com_faturamento ?? 0}
              percent={`${percentComFaturamento.toFixed(1)}%`}
              isActive={filters.get('has_billing') === 'with'}
              icon={cardConfig.comFaturamento.icon}
              iconColor={cardConfig.comFaturamento.iconColor}
              borderColor={cardConfig.comFaturamento.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (filters.get('has_billing') === 'with') {
                  newFilters.delete('has_billing');
                } else {
                  newFilters.set('page', '1');
                  newFilters.set('has_billing', 'with');
                  newFilters.delete('situacao'); // Remove filtro de situação
                }
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Sem Faturamento"
              value={situacoesTotais?.total_sem_faturamento ?? 0}
              percent={`${percentSemFaturamento.toFixed(1)}%`}
              isActive={filters.get('has_billing') === 'without'}
              icon={cardConfig.semFaturamento.icon}
              iconColor={cardConfig.semFaturamento.iconColor}
              borderColor={cardConfig.semFaturamento.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                if (filters.get('has_billing') === 'without') {
                  newFilters.delete('has_billing');
                } else {
                  newFilters.set('page', '1');
                  newFilters.set('has_billing', 'without');
                  newFilters.delete('situacao'); // Remove filtro de situação
                }
                setFilters(newFilters);
              }}
            />
            <TotalCard
              label="Total de Empresas"
              value={situacoesTotais?.total_empresas ?? 0}
              isActive={!filters.get('has_billing') && !situacaoFilter}
              icon={cardConfig.totalEmpresas.icon}
              iconColor={cardConfig.totalEmpresas.iconColor}
              borderColor={cardConfig.totalEmpresas.borderColor}
              onClick={() => {
                const newFilters = new URLSearchParams(Object.fromEntries(filters));
                newFilters.delete('has_billing');
                newFilters.delete('situacao'); // Remove filtro de situação também
                newFilters.set('page', '1');
                setFilters(newFilters);
              }}
            />
          </SimpleGrid>
        )}

        {/* Filtros no topo */}
        <Box mb={6} p={4} bg="gray.50" borderRadius="lg">
          <FormProvider {...filtersFormMethods}>
            {/* Linha principal: Pesquisa + Ordenação + Navegação de Mês + Botão Filtros */}
            <Flex
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align={{ base: 'stretch', md: 'center' }}
              gap={4}
            >
              {/* Campo de Pesquisa com Seletor de Modo */}
              <Flex
                direction={{ base: 'column', sm: 'row' }}
                gap={2}
                flex={1}
                maxW={{ base: '100%', md: '400px' }}
                align={{ base: 'stretch', sm: 'center' }}
              >
                <SearchModeBar
                  modes={MODES_ID_NOME_CNPJ}
                  mode={searchMode}
                  onModeChange={m => setSearchMode(m as typeof searchMode)}
                  value={filtersFormMethods.watch('search') ?? ''}
                  onValueChange={v =>
                    filtersFormMethods.setValue('search', v, { shouldDirty: true })
                  }
                />
              </Flex>

              {/* Ordenação, Navegação de Mês e Botão Filtros */}
              <HStack spacing={3} flexWrap="wrap">
                <Box minW={{ base: '100%', md: '220px' }}>
                  <SelectInputForm<ISelect<string>>
                    name="order_by"
                    options={[
                      { label: 'Maior variação', value: 'Maior variação de faturamento' },
                      { label: 'Menor variação', value: 'Menor variação de faturamento' },
                      { label: 'Maior fat. atual', value: 'Maior faturamento atual' },
                      { label: 'Menor fat. atual', value: 'Menor faturamento atual' },
                      { label: 'Maior fat. anterior', value: 'Maior faturamento anterior' },
                      { label: 'Menor fat. anterior', value: 'Menor faturamento anterior' },
                    ]}
                    placeholder="Ordenar por"
                    controlMb={0}
                    chakraStyles={{
                      control: (provided: any) => ({
                        ...provided,
                        height: '38px',
                        minHeight: '38px',
                      }),
                    }}
                  />
                </Box>
                <MonthNavigator
                  currentDate={currentFilterDate}
                  onPrevMonth={handlePrevMonth}
                  onNextMonth={handleNextMonth}
                  onSelectMonth={handleSelectMonth}
                />
                <Button
                  onClick={() => setIsFilterDrawerOpen(true)}
                  leftIcon={<FunnelSimple size={16} />}
                  variant="outline"
                  colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                  height="38px"
                >
                  Filtros{' '}
                  {hasActiveFilters &&
                    `(${Array.from(filters.keys()).filter(key => key !== 'page' && key !== 'size' && key !== 'date_start' && key !== 'date_end' && key !== 'situacao' && key !== 'has_billing' && key !== 'order' && key !== 'order_by' && key !== 'search' && key !== 'cnpj' && key !== 'external_id' && key !== 'screen_data' && filters.get(key)).length})`}
                </Button>
              </HStack>
            </Flex>

            {/* FilterDrawer para filtros avançados */}
            <FilterDrawer
              isOpen={isFilterDrawerOpen}
              onClose={() => setIsFilterDrawerOpen(false)}
              title="Filtros Avançados"
              hasActiveFilters={hasActiveFilters}
              onClearFilters={handleClearFilters}
              onApplyFilters={handleApplyFilters}
              formMethods={filtersFormMethods}
              size="lg"
            >
              <div className="grid grid-cols-2 gap-4">
                <SelectInputForm<ISelect<string>>
                  name="cidade"
                  placeholder="Filtrar por cidade"
                  options={getFormattedLabelString(CompaniesFilters?.cidade)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="uf"
                  placeholder="Filtrar por Estado"
                  options={getFormattedLabelString(CompaniesFilters?.uf)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="grupo_de_empresas"
                  placeholder="Filtrar por grupo de empresa"
                  options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="tags"
                  placeholder="Filtrar por tags"
                  options={getFormattedLabelString(CompaniesFilters?.tags)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="comercial"
                  placeholder="Filtrar por comercial"
                  options={getFormattedLabelString(CompaniesFilters?.comercial)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="contabil"
                  placeholder="Filtrar pelo contábil"
                  options={getFormattedLabelString(CompaniesFilters?.contabil)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="financeiro"
                  placeholder="Filtrar pelo financeiro"
                  options={getFormattedLabelString(CompaniesFilters?.financeiro)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="contas_a_pagar"
                  placeholder="Filtrar por contas à pagar"
                  options={getFormattedLabelString(CompaniesFilters?.contas_a_pagar)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="encerramento_anual"
                  placeholder="Filtrar por encerramento anual"
                  options={getFormattedLabelString(CompaniesFilters?.encerramento_anual)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="fiscal"
                  placeholder="Filtrar por fiscal"
                  options={getFormattedLabelString(CompaniesFilters?.fiscal)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="imposto_de_renda"
                  placeholder="filtrar por imposto de renda"
                  options={getFormattedLabelString(CompaniesFilters?.imposto_de_renda)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="integracao_contabil"
                  placeholder="Filtrar por integração contabil"
                  options={getFormattedLabelString(CompaniesFilters?.integracao_contabil)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="integracao_fiscal"
                  placeholder="Filtrar por integração fiscal"
                  options={getFormattedLabelString(CompaniesFilters?.integracao_fiscal)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="pessoal"
                  placeholder="Filtrar por pessoal"
                  options={getFormattedLabelString(CompaniesFilters?.pessoal)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="relacionamento_com_cliente"
                  placeholder="Filtrar por relacionamento com cliente"
                  options={getFormattedLabelString(CompaniesFilters?.relacionamento_com_cliente)}
                  controlMb={0}
                />
                <SelectInputForm<ISelect<string>>
                  name="societario"
                  placeholder="Filtrar por societário"
                  options={getFormattedLabelString(CompaniesFilters?.societario)}
                  controlMb={0}
                />
              </div>
            </FilterDrawer>
          </FormProvider>
        </Box>

        {/* Lista de Empresas */}
        {isFetchingCompanyList && allCompanies.length === 0 ? (
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={4}
            py={8}
            mt={{ base: 4, md: 6 }}
          >
            <Spinner size="lg" color="primary" />
            <Text fontSize="xl" fontWeight="semibold">
              Carregando...
            </Text>
          </Flex>
        ) : allCompanies.length === 0 && !isFetchingCompanyList ? (
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={4}
            py={8}
            mt={{ base: 4, md: 6 }}
          >
            <Text fontSize="lg" color="gray.500">
              Nenhum item encontrado
            </Text>
          </Flex>
        ) : (
          <Box mt={{ base: 4, md: 6 }}>
            {totalCompanies > 0 && (
              <Text mb={2} fontSize="sm" color="gray.600">
                Exibindo {allCompanies.length} de {totalCompanies} empresas
              </Text>
            )}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              {allCompanies.map((item, index) => {
                const variacao = item?.variacao_faturamento ?? 0;
                const borderColor =
                  variacao >= 0 && variacao < 20
                    ? '#10B981'
                    : variacao >= 20 && variacao <= 50
                      ? '#F59E0B'
                      : variacao > 50
                        ? '#EF4444'
                        : 'gray.300';
                const bgColor =
                  variacao >= 0 && variacao < 20
                    ? '#F0FDF4'
                    : variacao >= 20 && variacao <= 50
                      ? '#FEFCE8'
                      : variacao > 50
                        ? '#FEF2F2'
                        : 'white';

                return (
                  <Card
                    key={item?.external_id || index}
                    borderRadius="lg"
                    shadow="sm"
                    border="2px solid"
                    borderColor={borderColor}
                    bg={bgColor}
                  >
                    <CardBody p={{ base: 3, md: 4 }}>
                      {/* Header com botão */}
                      <Flex justifyContent="space-between" alignItems="flex-start" mb={3} gap={2}>
                        <Box flex={1} minW={0}>
                          <Text fontSize="xs" color="gray.500" mb={0.5} noOfLines={1}>
                            ID {item?.external_id} • {formatCNPJ(item?.cnpj)}
                          </Text>
                          <Text fontSize="sm" fontWeight="semibold" color="gray.700" noOfLines={1}>
                            {item?.razao_social}
                          </Text>
                        </Box>
                        <ViewNotaModal
                          screen="faturamento"
                          id={item?.id}
                          dateStart={dateStart}
                          dateEnd={dateEnd}
                          defaultTab="faturamento"
                          companyName={item?.razao_social}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            borderRadius="md"
                            border="1px solid"
                            borderColor="#718EBF"
                            bg="white"
                            fontSize={{ base: '11px', sm: '12px' }}
                            px={{ base: 2, sm: 3 }}
                            h="32px"
                            _hover={{ bg: 'gray.50' }}
                            flexShrink={0}
                            whiteSpace="nowrap"
                          >
                            <Text
                              color="#718EBF"
                              fontWeight="medium"
                              display={{ base: 'none', sm: 'block' }}
                            >
                              Ver anotações
                            </Text>
                            <Text
                              color="#718EBF"
                              fontWeight="medium"
                              display={{ base: 'block', sm: 'none' }}
                            >
                              Notas
                            </Text>
                          </Button>
                        </ViewNotaModal>
                      </Flex>

                      {/* Dados de Faturamento */}
                      <SimpleGrid columns={{ base: 2, sm: 3 }} spacing={3}>
                        <Box>
                          <Text fontSize="10px" color="gray.500" mb={0.5} textTransform="uppercase">
                            Variação
                          </Text>
                          <Text fontSize="lg" fontWeight="bold" color="#1A202C">
                            {parseInt(String(item?.variacao_faturamento ?? 0), 10)}%
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="10px" color="gray.500" mb={0.5} textTransform="uppercase">
                            Atual
                          </Text>
                          <Text fontSize="sm" fontWeight="semibold">
                            {toCurrency(item?.faturamento_atual)}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="10px" color="gray.500" mb={0.5} textTransform="uppercase">
                            Anterior
                          </Text>
                          <Text fontSize="sm" fontWeight="semibold">
                            {toCurrency(item?.faturamento_anterior)}
                          </Text>
                        </Box>
                      </SimpleGrid>
                    </CardBody>
                  </Card>
                );
              })}
            </SimpleGrid>

            {/* Elemento observado para carregar mais */}
            <Box ref={lastElementRef} py={4} textAlign="center">
              {isFetchingNextPage && (
                <Flex alignItems="center" justifyContent="center" gap={2}>
                  <Spinner size="md" color="blue.500" />
                  <Text color="gray.500">Carregando mais empresas...</Text>
                </Flex>
              )}
              {!hasNextPage && allCompanies.length > 0 && (
                <Text color="gray.400" fontSize="sm">
                  Todas as empresas foram carregadas
                </Text>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </PageBox>
  );
}
